import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import axios from "axios";
import { API } from "../App";

const LOGO_URL = "/images/singleseen_logo_transparent.png";

const VerifyEmailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useParams();
  
  const [status, setStatus] = useState("verifying"); // verifying, success, error

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setStatus("error");
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      await axios.post(`${API}/auth/verify-email`, { token });
      setStatus("success");
    } catch (error) {
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-pink-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center border border-rose-100">
        {/* Logo */}
        <div className="flex items-center justify-center mb-8">
          <img src={LOGO_URL} alt="SingleSeen" className="h-24 w-auto" />
        </div>

        {status === "verifying" && (
          <>
            <Loader2 className="w-16 h-16 text-rose-500 mx-auto mb-6 animate-spin" />
            <h1 className="font-serif text-2xl text-rose-900 mb-2">{t('verifyEmail.verifyingTitle')}</h1>
            <p className="text-rose-600">{t('verifyEmail.verifyingDesc')}</p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h1 className="font-serif text-2xl text-rose-900 mb-2" data-testid="verify-success">{t('verifyEmail.successTitle')}</h1>
            <p className="text-rose-600 mb-8">
              {t('verifyEmail.successDesc')}
            </p>
            <Button
              onClick={() => navigate("/dashboard")}
              className="w-full bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-full"
              data-testid="go-to-dashboard-btn"
            >
              {t('common.goToDashboard')}
            </Button>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="font-serif text-2xl text-rose-900 mb-2">{t('verifyEmail.failedTitle')}</h1>
            <p className="text-rose-600 mb-8">
              {t('verifyEmail.failedDesc')}
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate("/dashboard")}
                className="w-full bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-full"
              >
                {t('common.goToDashboard')}
              </Button>
              <Button
                onClick={() => navigate("/login")}
                variant="outline"
                className="w-full border-rose-300 text-rose-700 rounded-full"
              >
                {t('nav.backToLogin')}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmailPage;
